// src/components/MedicineMapping.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getSymptomMedicineMappingsAction,
  deleteSymptomMedicineMappingAction,
} from "../../../store/actions/SymptomMedicineMappingAction";
import swal from "sweetalert";
import Loader from "../Common/Loader";

const SymptomMedicineMapping = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mappings = useSelector(
    (state) => state.SymptomMedicineMappings?.mappings || []
  );


  const [searchQueries, setSearchQueries] = useState({
    symptom_medicine_id: "",
    symptom_name: "",
    medicine_name: "",
    medicine_type: "",
    status: "",
  });
  const [sortField, setSortField] = useState("symptom_medicine_id");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("")

  useEffect(() => {
    const fetchSymptomMedicine = async () => {
      setLoading(true);
      setError("");

      try {
        await dispatch(getSymptomMedicineMappingsAction());
      } catch (err) {
        console.error("Failed to fetch Symptom Medicine:", err);
        setError("Failed to load Symptom Medicine. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchSymptomMedicine();
  }, [dispatch]);


  const handleSearch = (e, field) => {
    setSearchQueries({ ...searchQueries, [field]: e.target.value });
    setCurrentPage(0);
  };


  const handleSort = (field) => {
    const nextOrder =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(nextOrder);
  };

  const handleEdit = (mapping) => {
    navigate("/add-medicinemapping", { state: { mapping } });
  };


  const handleDelete = (mapping) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this mapping!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          await dispatch(deleteSymptomMedicineMappingAction(mapping));
        } catch (err) {
          setError("Failed to delete Symptom Medicine Mapping");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };


  const filtered = mappings
    .filter((m) =>
      Object.entries(searchQueries).every(
        ([key, val]) =>
          !val ||
          m[key]?.toString().toLowerCase().includes(val.toLowerCase())
      )
    )
    .sort((a, b) => {
      const mod = sortOrder === "asc" ? 1 : -1;
      return (
        a[sortField]?.toString().localeCompare(b[sortField]?.toString()) *
        mod
      );
    });

  const paginated = filtered.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );
  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  return (
    <>

      <div className="row mb-3">
        <div className="col-xl-12 text-end">
          <Link to="/add-symptommedicinemapping" className="btn btn-primary btn-rounded">
            + Add Mapping
          </Link>
        </div>
      </div>


      <div className="card">
        {error && <div className="text-danger mb-2">{error}</div>}

        {loading ? (
          <div className="text-center my-5">
            <Loader size="lg" />
          </div>
        ) : (
          <>
            <table className="table table-striped mb-4">
              <thead>
                <tr>

                  <th
                    onClick={() => handleSort("symptom_medicine_id")}
                    style={{ cursor: "pointer" }}
                  >
                    ID{" "}
                    {sortField === "symptom_medicine_id" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                    <input
                      className="form-control mt-1"
                      value={searchQueries.symptom_medicine_id}
                      onChange={(e) =>
                        handleSearch(e, "symptom_medicine_id")
                      }
                      placeholder="Search ID"
                    />
                  </th>

                  <th
                    onClick={() => handleSort("symptom_name")}
                    style={{ cursor: "pointer" }}
                  >
                    Symptom{" "}
                    {sortField === "symptom_name" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                    <input
                      className="form-control mt-1"
                      value={searchQueries.symptom_name}
                      onChange={(e) => handleSearch(e, "symptom_name")}
                      placeholder="Search Symptom"
                    />
                  </th>

                  <th
                    onClick={() => handleSort("medicine_name")}
                    style={{ cursor: "pointer" }}
                  >
                    Medicine{" "}
                    {sortField === "medicine_name" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                    <input
                      className="form-control mt-1"
                      value={searchQueries.medicine_name}
                      onChange={(e) => handleSearch(e, "medicine_name")}
                      placeholder="Search Medicine"
                    />
                  </th>

                  <th
                    onClick={() => handleSort("medicine_type")}
                    style={{ cursor: "pointer" }}
                  >
                    Type{" "}
                    {sortField === "medicine_type" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                    <input
                      className="form-control mt-1"
                      value={searchQueries.medicine_type}
                      onChange={(e) => handleSearch(e, "medicine_type")}
                      placeholder="Search Type"
                    />
                  </th>

                  <th
                    onClick={() => handleSort("status")}
                    style={{ cursor: "pointer" }}
                  >
                    Status{" "}
                    {sortField === "status" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                    <input
                      className="form-control mt-1"
                      value={searchQueries.status}
                      onChange={(e) => handleSearch(e, "status")}
                      placeholder="Search Status"
                    />
                  </th>

                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {paginated.map((m) => (
                  <tr key={m.symptom_medicine_id}>
                    <td>{m.symptom_medicine_id}</td>
                    <td>{m.symptom_name}</td>
                    <td>{m.medicine_name}</td>
                    <td>{m.medicine_type}</td>
                    <td>{m.status}</td>
                    <td>
                      <div className="d-flex">
                        <span
                          className="me-3"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleEdit(m)}
                        >
                          <i className="fa fa-pencil-alt text-success"></i>
                        </span>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => handleDelete(m)}
                        >
                          <i className="fa fa-trash text-danger"></i>
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}

                {paginated.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center">
                      No mappings found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-between align-items-center">
              <div>
                Showing{" "}
                {filtered.length === 0
                  ? 0
                  : currentPage * rowsPerPage + 1}{" "}
                to{" "}
                {Math.min((currentPage + 1) * rowsPerPage, filtered.length)}{" "}
                of {filtered.length} entries
              </div>
              <div className="pagination d-flex">
                {Array.from({ length: totalPages }, (_, i) => (
                  <Link
                    to="#"
                    key={i}
                    className={`paginate_button ${currentPage === i ? "current" : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(i);
                    }}
                    style={{
                      marginRight: "5px",
                      padding: "5px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "3px",
                      backgroundColor: currentPage === i ? "#007bff" : "#fff",
                      color: currentPage === i ? "#fff" : "#000",
                      cursor: "pointer",
                    }}
                  >
                    {i + 1}
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SymptomMedicineMapping;
